import type { Map as MapLibre } from "maplibre-gl";

import { REALTIME } from "../config/global.configuration";
import { RealtimeCache } from "../model/RealtimeCache";
import { realtimeService } from "../services/RealtimeService";
import { logRealtimeMemoryReport } from "../tools/AppMemoryProfiler.tools";
import type { BBox } from "../types/dtos/realtime-api/realtime.dto";

export type DataUpdateCallback = () => void;

/**
 * Manages periodic polling of the Rust realtime API.
 * Fetches delta snapshots (only new shapes/trips), applies to cache,
 * and notifies listeners on data updates.
 *
 * @remarks **Anti-flicker contract (do not "optimize" away):** parallel snapshot polls,
 * skipping moveend refresh, or blindly applying `removed_trip_ids` caused buses to flash
 * on pan/zoom. Keep single-flight polling (`pollInFlight` / `pollQueued`), debounced
 * `poll()` on viewport settle, and client-side removal filtering in
 * {@link RealtimeCache.applySnapshot}. Removing these guardrails regresses UX.
 */
export class PollingManager {

    private map: MapLibre | null = null;

    private cache!: RealtimeCache;

    private pollIntervalId: ReturnType<typeof setInterval> | null = null;

    private evictIntervalId: ReturnType<typeof setInterval> | null = null;

    private debounceTimeoutId: ReturnType<typeof setTimeout> | null = null;

    private onDataUpdate: DataUpdateCallback | null = null;

    private initialized = false;

    /**
     * True while a snapshot request is in flight (one at a time, like legacy `loading.current`).
     * Do not remove: overlapping polls let stale bbox responses purge trips still on screen (flicker).
     */
    private pollInFlight = false;

    /**
     * When true, run another poll immediately after the current one finishes.
     * Do not remove: coalesces rapid moveend/poll bursts without parallel in-flight requests.
     */
    private pollQueued = false;

    constructor( cache: RealtimeCache ) {
        this.cache = cache;
    }

    /**
     * Binds the map instance used to derive viewport and zoom.
     *
     * @param map MapLibre instance used for bounds and zoom calculations.
     * @returns Nothing.
     */
    setMap( map: MapLibre ): void {
        this.map = map;
    }

    /**
     * Registers a callback executed after cache mutations.
     *
     * @param callback Callback invoked after snapshot application.
     * @returns Nothing.
     */
    setOnDataUpdate( callback: DataUpdateCallback ): void {
        this.onDataUpdate = callback;
    }

    /**
     * Initialize: fetch trip index + first snapshot in parallel.
     *
     * @returns Promise resolved when initial data fetch and cache hydration finish.
     */
    async init(): Promise<void> {
        if ( !this.map ) {
            return;
        }

        try {
            const bbox = this.getExpandedBbox();

            const [ initData, snapshotData ] = await Promise.all( [
                realtimeService.fetchInit(),
                realtimeService.fetchSnapshot(
                    bbox,
                    Date.now(),
                    this.cache.knownShapeIds,
                    this.cache.knownTripIds,
                ),
            ] );

            this.cache.loadTripIndex( initData.entries );
            this.cache.applySnapshot( snapshotData, this.getViewportContext() );
            this.initialized = true;

            this.onDataUpdate?.();

            console.log( `[PollingManager] Initialized: ${ initData.trip_count } trips indexed, ` +
              `${ snapshotData.new_shapes.length } shapes, ` +
              `${ snapshotData.new_trips.length } trips loaded` );

            logRealtimeMemoryReport( {
                "context": "init",
                "cache":   this.cache,
            } );
        } catch( error ) {
            console.error( "[PollingManager] Init failed:", error );
        }
    }

    /**
     * Start periodic polling.
     *
     * @returns Nothing.
     */
    start(): void {
        if ( this.pollIntervalId !== null ) {
            return;
        }

        this.pollIntervalId = setInterval( () => {
            void this.poll();
        }, REALTIME.pollIntervalMs );

        this.evictIntervalId = setInterval( () => {
            this.cache.evictStale();
            logRealtimeMemoryReport( {
                "context": "evict-stale",
                "cache":   this.cache,
            } );
        }, REALTIME.evictIntervalMs );
    }

    /**
     * Stop polling.
     *
     * @returns Nothing.
     */
    stop(): void {
        if ( this.pollIntervalId !== null ) {
            clearInterval( this.pollIntervalId );
            this.pollIntervalId = null;
        }
        if ( this.evictIntervalId !== null ) {
            clearInterval( this.evictIntervalId );
            this.evictIntervalId = null;
        }
    }

    /**
     * Pause polling (e.g., when tab is hidden).
     *
     * @returns Nothing.
     */
    pause(): void {
        this.stop();
    }

    /**
     * Resume polling (e.g., when tab becomes visible).
     *
     * @remarks The eviction interval does not run while paused, so after a long
     * background period the cache still holds hours of expired trips and shapes.
     * They are purged before the first poll, otherwise the vehicle layer iterates
     * the stale backlog on top of the fresh snapshot on every frame until the next
     * eviction tick, which shows up as sustained jank right after resume.
     *
     * @returns Nothing.
     */
    resume(): void {
        this.cache.evictStale();
        void this.poll();
        this.start();
    }

    /**
     * Handle viewport changes after pan/zoom settles.
     * Debounced full snapshot refresh (legacy `loadVehiclesInView` on `moveend`).
     *
     * Do not replace with "poll only when N trips are missing": that was tried as a network
     * optimization and brought back bus flicker on pan/zoom. Delta snapshots are cheap when idle.
     *
     * @returns Nothing.
     */
    onViewportChange(): void {
        if ( !this.initialized || !this.map ) {
            return;
        }

        if ( this.debounceTimeoutId !== null ) {
            clearTimeout( this.debounceTimeoutId );
        }

        this.debounceTimeoutId = setTimeout( () => {
            this.debounceTimeoutId = null;
            void this.poll();
        }, REALTIME.viewportDebounceMs );
    }

    /**
     * Destroy and clean up.
     *
     * @returns Nothing.
     */
    destroy(): void {
        this.stop();
        if ( this.debounceTimeoutId !== null ) {
            clearTimeout( this.debounceTimeoutId );
        }
    }

    /**
     * Executes one polling cycle and applies the returned snapshot to cache.
     *
     * @returns Promise resolved when one polling cycle completes.
     */
    private async poll(): Promise<void> {
        if ( !this.map || !this.initialized ) {
            return;
        }

        // Single-flight + queue: required anti-flicker behavior (see class @remarks).
        if ( this.pollInFlight ) {
            this.pollQueued = true;

            return;
        }

        this.pollInFlight = true;

        try {
            do {
                this.pollQueued = false;

                const bbox = this.getExpandedBbox();

                const snapshot = await realtimeService.fetchSnapshot(
                    bbox,
                    Date.now(),
                    this.cache.knownShapeIds,
                    this.cache.knownTripIds,
                );

                this.cache.applySnapshot( snapshot, this.getViewportContext() );
                this.onDataUpdate?.();
            } while ( this.pollQueued );
        } catch( error ) {
            console.error( "[PollingManager] Poll failed:", error );
        } finally {
            this.pollInFlight = false;
        }
    }

    /**
     * Returns bbox and time used when validating snapshot removals on the client.
     *
     * @returns Viewport context for {@link RealtimeCache.applySnapshot}.
     */
    private getViewportContext(): {
        "bbox":          BBox;
        "currentTimeMs": number;
    } {
        return {
            "bbox":          this.getExpandedBbox(),
            "currentTimeMs": Date.now(),
        };
    }

    /**
     * Returns the current map bounds expanded by the configured prefetch factor.
     *
     * @returns Expanded bounding box used for realtime snapshot requests.
     */
    private getExpandedBbox(): BBox {
        if ( !this.map ) {
            return {
                "minLat": 0,
                "minLon": 0,
                "maxLat": 0,
                "maxLon": 0,
            };
        }

        const bounds = this.map.getBounds();
        const latSpan = bounds.getNorth() - bounds.getSouth();
        const lonSpan = bounds.getEast() - bounds.getWest();
        const latPad = latSpan * ( REALTIME.viewportExpandFactor - 1 ) / 2;
        const lonPad = lonSpan * ( REALTIME.viewportExpandFactor - 1 ) / 2;

        return {
            "minLat": bounds.getSouth() - latPad,
            "minLon": bounds.getWest() - lonPad,
            "maxLat": bounds.getNorth() + latPad,
            "maxLon": bounds.getEast() + lonPad,
        };
    }

}
